import { gql, useLazyQuery } from "@apollo/client";
import { useToast } from "@resource/atlas";
import { useFlags } from "@resource/client-ffs";
import { strings } from "@resource/common";
import { useAuthContext } from "auth/context";
import Comments from "components/legacy/Feedback/Comments/Comments";
import FEEDBACK_FOR_ORGANIZATION from "components/legacy/Feedback/FEEDBACK_FOR_ORGANIZATION";
import SortBar, {
  DefaultFilterTimeRange,
  FilterParams,
  filtersDifferFromDefault,
  FilterTimeRangeEnum,
} from "components/legacy/Feedback/SortBar/SortBar";
import Summary from "components/legacy/Feedback/Summary/Summary";
import { CardData } from "components/legacy/Masonry/Masonry";
import LinkList from "components/LinkList";
import SurveyHelpfulLinks from "components/LinkList/SurveyHelpfulLinks";
import NoTableResults from "components/Table/NoTableResults";
import FeatureFlags from "generated/FeatureFlags";
import {
  ExportCandidateFeedback,
  ExportCandidateFeedbackVariables,
  FeedbackForOrganizationInputPrismaProxy,
  FeedbackForOrganizationPrismaProxy,
  FeedbackForOrganizationPrismaProxyVariables,
} from "generated/schemaTypes";
import _ from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import useTextSearch from "react-hooks/legacy/useTextSearch";
import { formatEntity } from "shared/constants/entities";
import useMutation from "utils/useMutation";

const EXPORT_FEEDBACK = gql`
  mutation ExportCandidateFeedback(
    $input: GenerateCandidateFeedbackExportInputPrismaProxy!
  ) {
    generateCandidateFeedbackExport(input: $input) {
      success
      url
    }
  }
`;

const DefaultFilters = {
  timeRange: DefaultFilterTimeRange,
  roleIds: [],
  stageNames: [],
  departmentIds: [],
};

export default function Feedback() {
  const { sendToast } = useToast();
  const { [FeatureFlags.NEW_FEEDBACK_ALGORITHM]: newFeedbackAlgorithmEnabled } =
    useFlags();
  const {
    zeusInfo: { isZeus },
  } = useAuthContext();
  const [getFeedback, { data, loading }] = useLazyQuery<
    FeedbackForOrganizationPrismaProxy,
    FeedbackForOrganizationPrismaProxyVariables
  >(FEEDBACK_FOR_ORGANIZATION);

  const [exportCandidateFeedback] = useMutation<
    ExportCandidateFeedback,
    ExportCandidateFeedbackVariables
  >(EXPORT_FEEDBACK);

  const candidateFeedbackStats = data?.feedbackForOrganizationPrismaProxy.stats;
  const averageNps = candidateFeedbackStats?.averageNps;
  const enableNPS =
    !!data?.feedbackForOrganizationPrismaProxy.organization?.enableNPS;
  const averageRating = candidateFeedbackStats?.averageRating;
  const whatWentWell = candidateFeedbackStats?.whatWentWell;
  const whatCouldBeBetter = candidateFeedbackStats?.whatCouldBeBetter;
  const comments = candidateFeedbackStats?.comments;
  const totalCount = candidateFeedbackStats?.totalCount ?? 0;
  const totalActiveInAts = candidateFeedbackStats?.activeInAtsCount ?? 0;

  const [filterParams, setFilterParams] =
    useState<FilterParams>(DefaultFilters);
  const [searchParams, setSearchParams] = useState<string>("");
  const [filteredWhatWentWellComments] = useTextSearch({
    queryString: searchParams,
    searchArray: comments || [],
    searchKey: "whatWentWellFreeform",
  });
  const [filteredCouldBeBetterComments] = useTextSearch({
    queryString: searchParams,
    searchArray: comments || [],
    searchKey: "whatCouldBeBetterFreeform",
  });
  const filteredComments = _.unionBy(
    filteredWhatWentWellComments,
    filteredCouldBeBetterComments,
    "id"
  );

  const [emptyStatement, setEmptyStatement] = useState<string>("");

  useEffect(() => {
    if (comments && comments.length === 0) {
      setEmptyStatement(
        "🤔 Looks like there isn't enough data. Try changing the filters."
      );
    } else if (filteredComments.length === 0) {
      setEmptyStatement("No comments match your search");
    }
  }, [filteredComments, comments]);

  const calculateTime = (selectedTime: FilterTimeRangeEnum): string => {
    switch (selectedTime) {
      case FilterTimeRangeEnum.last1Month:
        return moment().subtract(1, "month").toISOString();
      case FilterTimeRangeEnum.last3Months:
        return moment().subtract(3, "month").toISOString();
      case FilterTimeRangeEnum.last6Months:
        return moment().subtract(6, "month").toISOString();
      case FilterTimeRangeEnum.lastYear:
        return moment().subtract(12, "month").toISOString();
      default:
        return moment().subtract(3, "month").toISOString();
    }
  };

  useEffect(() => {
    const feedbackQueryInput: FeedbackForOrganizationInputPrismaProxy = {
      startTimeRange: calculateTime(filterParams.timeRange),
    };

    if (filterParams.roleIds.length) {
      feedbackQueryInput.jobRoleIds = filterParams.roleIds;
    }
    if (filterParams.departmentIds.length) {
      feedbackQueryInput.departmentIds = filterParams.departmentIds;
    }
    if (filterParams.stageNames.length) {
      feedbackQueryInput.stageNames = filterParams.stageNames;
    }

    getFeedback({
      variables: {
        input: {
          ...feedbackQueryInput,
        },
      },
    });
  }, [filterParams, getFeedback]);

  const clearFilters = () => {
    setFilterParams(DefaultFilters);
  };

  const exportFeedback = async (format: "csv" | "xls") => {
    const res = await exportCandidateFeedback({
      variables: {
        input: {
          startTimeRange: calculateTime(filterParams.timeRange),
          jobRoleIds: filterParams.roleIds,
          format,
        },
      },
    });
    window.open(res.data?.generateCandidateFeedbackExport?.url ?? "", "_blank");
    sendToast(
      "Successfully exported your candidate feedback. Your download should begin shortly.",
      {
        variant: "success",
      }
    );
  };

  const anyFiltersSet = filtersDifferFromDefault(filterParams);

  const helpfulLinks = [
    SurveyHelpfulLinks.getStarted,
    SurveyHelpfulLinks.takeTour,
    SurveyHelpfulLinks.watchVideo,
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-display-2">
        Candidate{" "}
        {isZeus
          ? formatEntity("survey", { plural: true, capitalize: true })
          : "Feedback"}
      </h1>
      {isZeus && <LinkList links={helpfulLinks} />}

      <SortBar
        filterParams={filterParams}
        setFilterParams={setFilterParams}
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        clearFilters={clearFilters}
        exportFeedback={exportFeedback}
      />
      {newFeedbackAlgorithmEnabled && (
        <div className="space-y-4">
          {totalActiveInAts > 0 ? (
            <div className="flex justify-between items-center bg-blue-50 py-4 px-4 rounded-lg">
              <div className="flex items-center text-blue-900 text-body-md gap-3">
                <p className="text-3xl">💬</p>
                {isZeus ? (
                  <p>
                    You have{" "}
                    <strong>
                      {strings.pluralize(
                        `${formatEntity("survey")} submission`,
                        totalActiveInAts
                      )}
                    </strong>{" "}
                    from candidates who are actively interviewing. Survey
                    results will appear below once they&apos;ve been hired or
                    rejected.
                  </p>
                ) : (
                  <p>
                    You have{" "}
                    <strong>
                      {strings.pluralize(
                        "feedback submission",
                        totalActiveInAts
                      )}
                    </strong>{" "}
                    from active candidates. Their feedback will be shown below
                    once they have been hired or rejected.
                  </p>
                )}
              </div>
            </div>
          ) : null}
          {totalCount > 0 && (
            <p className="text-body-md text-subtle">
              Reporting based on{" "}
              {strings.pluralize("candidate response", totalCount)}.
            </p>
          )}
          <NoTableResults
            loading={loading}
            hasResults={totalCount > 0}
            isFiltered={anyFiltersSet}
            clearFilters={clearFilters}
            notFilteredHeading="Hang tight! Candidate feedback is on the way."
            notFileredDescription="To protect the identity of candidates and increase the number of survey submissions you receive, results will appear once a hiring decision is made."
          />
        </div>
      )}
      {(totalCount > 0 || !newFeedbackAlgorithmEnabled) && (
        <>
          <Summary
            averageNps={averageNps}
            averageRating={averageRating}
            whatCouldBeBetter={whatCouldBeBetter || []}
            whatWentWell={whatWentWell || []}
            enableNPS={enableNPS}
            loading={loading}
          />
          <Comments
            comments={(filteredComments as CardData[]) || []}
            loading={loading}
            emptyStatement={emptyStatement}
          />
        </>
      )}
    </div>
  );
}
